import { useEffect, useRef, useState } from "react";

import { LIVE_RUNS_COUNTDOWN_TICK_MS } from "./constants";

export interface UseLiveRunsListenOptions {
  /** Opens the window; resolves with the server's expiry (epoch ms). */
  start: (windowMinutes?: number) => Promise<{ listenWindowExpiresAt: number }>;
  /** Closes the window. */
  stop: () => Promise<void>;
  /**
   * The server's view of the window, as carried by every polled run listing
   * (`LiveRunsListingWindowSchema`). `undefined` while the listing has not
   * loaded; `null` when no window is open. Every defined update wins, so a
   * window closed elsewhere (another tab, the TTL) is noticed within a poll.
   */
  serverListenWindowExpiresAt: number | null | undefined;
  /** Open on mount and close on unmount (re-keyed on `resetKey`). */
  autoStart?: boolean;
  /**
   * Identity of the listened-to entity. Changing it drops the local expiry
   * (and, with `autoStart`, closes the old window and opens a new one).
   */
  resetKey: string | undefined;
}

export interface UseLiveRunsListenResult {
  /** Epoch ms the window closes, or null when not listening. */
  expiresAt: number | null;
  /** Epoch ms "now", ticking once a second while listening. */
  now: number;
  isListening: boolean;
  /** `true` while a start or stop request is in flight. */
  isToggling: boolean;
  open: (windowMinutes?: number) => void;
  close: () => void;
}

/**
 * Listen-window state for a live-runs view, shared by every codegen domain.
 * The expiry comes from the start response first and from every server
 * update after that; a one-second tick keeps the countdown moving only while
 * a window is open. Start/stop errors are the caller's mutation's to toast —
 * the hook just clears its pending flag.
 */
export const useLiveRunsListen = ({
  start,
  stop,
  serverListenWindowExpiresAt,
  autoStart = false,
  resetKey,
}: UseLiveRunsListenOptions): UseLiveRunsListenResult => {
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [pendingCount, setPendingCount] = useState(0);
  const isListening = expiresAt !== null && expiresAt > now;

  // Callers typically pass fresh closures every render; the mount/unmount
  // effect must not re-run for that, so it reads them through refs.
  const startRef = useRef(start);
  startRef.current = start;
  const stopRef = useRef(stop);
  stopRef.current = stop;

  const track = async <T>(op: Promise<T>): Promise<T | undefined> => {
    setPendingCount((n) => n + 1);
    try {
      return await op;
    } catch {
      return undefined;
    } finally {
      setPendingCount((n) => n - 1);
    }
  };

  const open = (windowMinutes?: number) => {
    void track(startRef.current(windowMinutes)).then((result) => {
      if (result) {
        setNow(Date.now());
        setExpiresAt(result.listenWindowExpiresAt);
      }
    });
  };
  const close = () => {
    setExpiresAt(null);
    void track(stopRef.current());
  };

  // A new entity means a new window: drop the local expiry and, when
  // auto-starting, open for it (closing on cleanup; the store's TTL is the
  // backstop if the DELETE never lands).
  useEffect(() => {
    setExpiresAt(null);
    if (!autoStart) {
      return;
    }
    open();
    return () => close();
  }, [resetKey, autoStart]);

  useEffect(() => {
    if (serverListenWindowExpiresAt === undefined) {
      return;
    }
    setNow(Date.now());
    setExpiresAt(serverListenWindowExpiresAt);
  }, [serverListenWindowExpiresAt]);

  useEffect(() => {
    if (!isListening) {
      return;
    }
    const timer = setInterval(
      () => setNow(Date.now()),
      LIVE_RUNS_COUNTDOWN_TICK_MS
    );
    return () => clearInterval(timer);
  }, [isListening]);

  return {
    expiresAt,
    now,
    isListening,
    isToggling: pendingCount > 0,
    open,
    close,
  };
};
